import React, { useMemo, useEffect } from "react"; // Added useEffect
import Task from "./Task";

// Default settings object to prevent errors if settings prop is undefined or incomplete
const defaultSettingsValues = {
  useTags: false,
  selectedFilterTags: [],
  useTaskPriority: false,
  addTaskOnEnter: true,
  autoCompleteOnTimer: true,
  defaultTaskDuration: 1800,
  tags: [],
  useNotes: false,
};

// Component for rendering filtered task lists
function TaskList({
  level,
  tasks,
  settings = defaultSettingsValues, // Provide a default value for the settings prop
  parentId,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  onSelectTask,
  selectedPath,
  taskRefs,
}) {
  // Log the received settings.useTaskPriority when the component renders or settings change
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[TaskList] Received settings.useTaskPriority: ${
          settings.useTaskPriority
        } for tasks in column of parent: ${parentId || "Main"}`
      );
    }
  }, [settings.useTaskPriority, parentId]);

  const filterTasksInternal = (tasksToFilter, currentSettings) => {
    if (
      !currentSettings.useTags ||
      !currentSettings.selectedFilterTags ||
      currentSettings.selectedFilterTags.length === 0
    ) {
      return tasksToFilter;
    }
    return tasksToFilter
      .filter((task) => {
        const taskTags = task.tags || [];
        return currentSettings.selectedFilterTags.every((tag) =>
          taskTags.includes(tag)
        );
      })
      .map((task) => ({
        ...task,
        children: task.children
          ? filterTasksInternal(task.children, currentSettings)
          : [],
      }));
  };

  const sortTasksByPriorityInternal = (tasksToSort, currentSettings) => {
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[TaskList] sortTasksByPriorityInternal called. currentSettings.useTaskPriority: ${currentSettings.useTaskPriority}`
      );
    }
    if (!currentSettings.useTaskPriority) {
      // This is the crucial check
      if (process.env.NODE_ENV === "development") {
        console.log(
          "[TaskList] Priority sorting SKIPPED because useTaskPriority is false."
        );
      }
      return tasksToSort;
    }
    if (process.env.NODE_ENV === "development") {
      console.log(
        "[TaskList] Priority sorting APPLIED because useTaskPriority is true."
      );
    }
    return [...tasksToSort].sort((a, b) => {
      const priorityA =
        a.priority === null || a.priority === undefined ? Infinity : a.priority;
      const priorityB =
        b.priority === null || b.priority === undefined ? Infinity : b.priority;

      if (priorityA < priorityB) return -1;
      if (priorityA > priorityB) return 1;
      return 0;
    });
  };

  const filteredTasks = useMemo(
    () => filterTasksInternal(tasks, settings),
    [tasks, settings]
  );

  const sortedTasks = useMemo(
    () => {
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[TaskList useMemo sortedTasks] About to sort. settings.useTaskPriority: ${settings.useTaskPriority}`
        );
      }
      return sortTasksByPriorityInternal(filteredTasks, settings);
    },
    [filteredTasks, settings] // Correctly depends on the whole settings object
  );

  const incompleteTasks = useMemo(
    () => sortedTasks.filter((task) => !task.completed),
    [sortedTasks]
  );
  const completedTasks = useMemo(
    () => sortedTasks.filter((task) => task.completed),
    [sortedTasks]
  );

  return (
    <>
      {incompleteTasks.map((task) => (
        <Task
          key={task.id}
          task={task}
          parentId={parentId}
          onAddTask={onAddTask}
          onUpdateTask={onUpdateTask}
          onDeleteTask={onDeleteTask}
          onAddChildTask={(childParentId, newTask) =>
            onAddTask(childParentId, newTask)
          }
          onSelectTask={(id) => onSelectTask(id, level)}
          isSelected={selectedPath.includes(task.id)}
          settings={settings}
          tags={settings.tags}
          useTags={settings.useTags}
          useNotes={settings.useNotes}
          addTaskOnEnter={settings.addTaskOnEnter}
          autoCompleteOnTimer={settings.autoCompleteOnTimer}
          defaultTaskDuration={settings.defaultTaskDuration}
          taskRef={(ref) => (taskRefs.current[task.id] = ref)}
          taskRefs={taskRefs}
          selectedPath={selectedPath}
        />
      ))}
      {completedTasks.length > 0 && (
        <>
          <hr className="completed-separator" />
          {completedTasks.map((task) => (
            <Task
              key={task.id}
              task={task}
              parentId={parentId}
              onAddTask={onAddTask}
              onUpdateTask={onUpdateTask}
              onDeleteTask={onDeleteTask}
              onAddChildTask={(childParentId, newTask) =>
                onAddTask(childParentId, newTask)
              }
              onSelectTask={(id) => onSelectTask(id, level)}
              isSelected={selectedPath.includes(task.id)}
              settings={settings}
              tags={settings.tags}
              useTags={settings.useTags}
              useNotes={settings.useNotes}
              addTaskOnEnter={settings.addTaskOnEnter}
              autoCompleteOnTimer={settings.autoCompleteOnTimer}
              defaultTaskDuration={settings.defaultTaskDuration}
              taskRef={(ref) => (taskRefs.current[task.id] = ref)}
              taskRefs={taskRefs}
              selectedPath={selectedPath}
            />
          ))}
        </>
      )}
    </>
  );
}

export default TaskList;
